import { useKnowledgeBase } from '@app/lib'
import { useSugarContext } from '@app/providers'
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  createStyles,
  Group,
  LoadingOverlay,
  Select,
  Stack,
  type StackProps,
  Table,
  Text
} from '@mantine/core'
import { IconAlertCircle, IconDownload } from '@tabler/icons-react'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { first, map } from 'lodash'
import { useRouter } from 'next/router'
import { type FC, useCallback, useMemo } from 'react'

dayjs.extend(localizedFormat)

const useStyles = createStyles(() => ({
  actionColumn: {
    textAlign: 'center',
    verticalAlign: 'middle',
    width: 64
  },
  fitWidth: {
    whiteSpace: 'nowrap',
    width: 1
  }
}))

export interface KnowledgeBaseTableProps extends Omit<StackProps, 'children'> {}

export const KnowledgeBaseTable: FC<KnowledgeBaseTableProps> = (props) => {
  const { classes } = useStyles()
  const router = useRouter()
  const { query: routerQuery, replace: routerReplace } = router
  const { rosterProgramYears } = useSugarContext()

  // KB is a global listing (MIQ-2094). If ?programYear is present, use that query value.
  // Otherwise, default to the site's first roster program year so the Select displays a
  // value instead of appearing blank before roster program years have been chosen by the user.
  // This component therefore typically sends a fallback programYearId when one is available.
  const programYearId =
    (typeof routerQuery.programYear === 'string' ? routerQuery.programYear : null) ??
    first(rosterProgramYears)?.id ??
    null

  const setProgramYearId = useCallback(
    (next: string | null) => {
      if (!next || next === programYearId) return

      void routerReplace({ pathname: router.pathname, query: { ...routerQuery, programYear: next } }, undefined, {
        shallow: true
      })
    },
    [programYearId, router.pathname, routerQuery, routerReplace]
  )

  const programYearOptions = useMemo(
    () =>
      map(rosterProgramYears, (py) => ({
        label: py.name,
        value: py.id
      })),
    [rosterProgramYears]
  )

  const { data, isError, isFetching, refetch, error } = useKnowledgeBase({
    programYearId: programYearId ?? undefined
  })

  return (
    <Stack
      pos='relative'
      spacing='lg'
      {...props}
    >
      <LoadingOverlay visible={isFetching} />
      {rosterProgramYears.length > 0 && (
        <Group
          align='center'
          grow
          position='center'
        >
          <Select
            data={programYearOptions}
            label='Program Year'
            onChange={setProgramYearId}
            value={programYearId}
            variant='default'
          />
        </Group>
      )}
      {isError ? (
        <Alert
          color='red'
          icon={<IconAlertCircle size={16} />}
          title='Could not load knowledge base documents'
        >
          <Stack spacing='sm'>
            <Text size='sm'>
              {(error as Error | null)?.message ?? 'Something went wrong loading the knowledge base. Please try again.'}
            </Text>
            <Box>
              <Button
                color='red'
                onClick={() => refetch()}
                size='xs'
                variant='light'
              >
                Retry
              </Button>
            </Box>
          </Stack>
        </Alert>
      ) : (
        <Table
          horizontalSpacing='xs'
          verticalSpacing='xs'
          withBorder
        >
          <thead>
            <tr>
              <th>Document Name</th>
              <th>Description</th>
              <th className={classes.fitWidth}>Date</th>
              <th className={classes.actionColumn}>Action</th>
            </tr>
          </thead>
          <tbody>
            {data?.data?.length ? (
              map(data.data, (doc) => (
                <tr key={doc.id}>
                  <td>{doc.documentName}</td>
                  <td>{doc.description ?? '—'}</td>
                  <td className={classes.fitWidth}>{doc.documentDate ? dayjs(doc.documentDate).format('LL') : '—'}</td>
                  <td className={classes.actionColumn}>
                    <ActionIcon
                      color='brand-red'
                      component='a'
                      href={`/api/knowledge-base/${doc.downloadId}`}
                      tabIndex={0}
                      title='Download Knowledge Base Document'
                      variant='transparent'
                    >
                      <IconDownload size={16} />
                    </ActionIcon>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4}>
                  <Text
                    align='center'
                    color='dimmed'
                  >
                    No knowledge base documents for this program year yet.
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </Stack>
  )
}
